import { ComponentProps, ReactNode } from "react";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { EmptyState } from "../EmptyState/EmptyState";
import { TableSkeleton } from "../EmptyState/TableSkeleton";
import {
  ROW_NUMBER_BADGE_SX,
  ROW_NUMBER_WIDTH_PCT,
  TABLE_BODY_CELL_SX,
  TABLE_HEADER_CELL_SX,
  TABLE_PAGINATION_SX,
  TABLE_PAPER_SX,
  TABLE_ROW_HOVER_SX,
  TABLE_ROWS_PER_PAGE_OPTIONS,
  TABLE_TOOLBAR_SX,
} from "./styles";

export type AppTableColumn = {
  id: string;
  label: ReactNode;
  sortable?: boolean;
  disablePadding?: boolean;
  widthPct?: number;
  align?: "left" | "right" | "center";
};

export type AppTablePagination = {
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: number[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

type AppTableProps = {
  columns: AppTableColumn[];
  children: ReactNode;
  showRowNumber?: boolean;
  /** Content before the # / data columns (e.g. select-all checkbox). */
  leadingHeadCell?: ReactNode;
  /** Content after data columns (e.g. Actions). */
  trailingHeadCells?: ReactNode;
  sortBy?: string;
  sortAsc?: boolean;
  onRequestSort?: (columnId: string) => void;
  toolbar?: ReactNode;
  loading?: boolean;
  loadingMessage?: string;
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  /** Extra columns for empty/loading colspan (checkbox, actions, etc.). */
  extraColumnCount?: number;
  pagination?: AppTablePagination;
  stickyHeader?: boolean;
  size?: "small" | "medium";
  maxHeight?: number | string;
  /** When true, Paper grows to fill a flex parent. */
  fillHeight?: boolean;
};

/** Body cell with shared table cell styles. */
export function AppTableCell({ sx, ...props }: TableCellProps) {
  return <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...sx }} {...props} />;
}

/** Header cell with shared table header styles. */
export function AppTableHeadCell({ sx, ...props }: TableCellProps) {
  return <TableCell sx={{ ...TABLE_HEADER_CELL_SX, ...sx }} {...props} />;
}

export function RowNumberCell({
  value,
  rowSpan,
}: {
  value: number | string;
  rowSpan?: number;
}) {
  return (
    <AppTableCell
      rowSpan={rowSpan}
      sx={{
        width: `${ROW_NUMBER_WIDTH_PCT}%`,
        verticalAlign: "middle",
      }}
    >
      <Box sx={ROW_NUMBER_BADGE_SX}>{value}</Box>
    </AppTableCell>
  );
}

export function AppTable({
  columns,
  children,
  showRowNumber = true,
  leadingHeadCell,
  trailingHeadCells,
  sortBy,
  sortAsc = true,
  onRequestSort,
  toolbar,
  loading = false,
  loadingMessage: _loadingMessage = "Loading…",
  isEmpty = false,
  emptyTitle = "No results",
  emptyDescription,
  emptyAction,
  extraColumnCount = 0,
  pagination,
  stickyHeader = true,
  size = "small",
  maxHeight,
  fillHeight = false,
}: AppTableProps) {
  const colSpan =
    columns.length +
    (showRowNumber ? 1 : 0) +
    (leadingHeadCell ? 1 : 0) +
    extraColumnCount;

  return (
    <Paper
      variant="outlined"
      sx={{
        ...TABLE_PAPER_SX,
        ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
      }}
    >
      {toolbar && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={TABLE_TOOLBAR_SX}
          >
            {toolbar}
          </Stack>
          <Divider />
        </>
      )}

      <TableContainer
        sx={{
          ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
          overflowY: "auto",
          overflowX: "auto",
          maxHeight,
        }}
      >
        <Table
          size={size}
          stickyHeader={stickyHeader}
          sx={{
            width: "100%",
            tableLayout: columns.some((c) => c.widthPct != null)
              ? "fixed"
              : "auto",
            "& .MuiTableCell-stickyHeader": {
              boxShadow: "0 1px 0 rgba(15, 23, 42, 0.08)",
            },
          }}
        >
          {columns.some((c) => c.widthPct != null) && (
            <colgroup>
              {leadingHeadCell && <col style={{ width: "48px" }} />}
              {showRowNumber && (
                <col style={{ width: `${ROW_NUMBER_WIDTH_PCT}%` }} />
              )}
              {columns.map((column) => (
                <col
                  key={column.id}
                  style={
                    column.widthPct != null
                      ? { width: `${column.widthPct}%` }
                      : undefined
                  }
                />
              ))}
            </colgroup>
          )}

          <TableHead>
            <TableRow>
              {leadingHeadCell}
              {showRowNumber && (
                <AppTableHeadCell
                  sx={{ width: `${ROW_NUMBER_WIDTH_PCT}%` }}
                >
                  #
                </AppTableHeadCell>
              )}
              {columns.map((column) => {
                const canSort = column.sortable !== false && !!onRequestSort;
                return (
                  <AppTableHeadCell
                    key={column.id}
                    align={column.align ?? "left"}
                    padding={column.disablePadding ? "none" : "normal"}
                    sx={
                      column.widthPct != null
                        ? { width: `${column.widthPct}%` }
                        : undefined
                    }
                    sortDirection={
                      sortBy === column.id ? (sortAsc ? "asc" : "desc") : false
                    }
                  >
                    {canSort ? (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={
                          sortBy === column.id
                            ? sortAsc
                              ? "asc"
                              : "desc"
                            : "asc"
                        }
                        onClick={() => onRequestSort?.(column.id)}
                      >
                        {column.label}
                        {sortBy === column.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {sortAsc ? "sorted ascending" : "sorted descending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </AppTableHeadCell>
                );
              })}
              {trailingHeadCells}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableSkeleton rows={6} columns={colSpan} />
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                  <EmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                    action={emptyAction}
                    compact
                  />
                </TableCell>
              </TableRow>
            ) : (
              children
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.count}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={
            pagination.rowsPerPageOptions ?? [...TABLE_ROWS_PER_PAGE_OPTIONS]
          }
          onPageChange={(_, nextPage) => pagination.onPageChange(nextPage)}
          onRowsPerPageChange={(e) =>
            pagination.onRowsPerPageChange(parseInt(e.target.value, 10))
          }
          sx={TABLE_PAGINATION_SX}
        />
      )}
    </Paper>
  );
}

/** Hover-friendly row using shared row styles. */
export function AppTableRow({
  hover = true,
  sx,
  ...props
}: ComponentProps<typeof TableRow>) {
  return (
    <TableRow
      hover={hover}
      sx={{ ...TABLE_ROW_HOVER_SX, ...sx }}
      {...props}
    />
  );
}

/** @deprecated Prefer AppTable — kept for existing call sites during migration. */
export const StyledDataTable = AppTable;
/** @deprecated Prefer AppTableColumn */
export type StyledDataTableColumn = AppTableColumn;
/** @deprecated Prefer AppTablePagination */
export type StyledDataTablePagination = AppTablePagination;
